import { cn } from "@gymflow/helpers";
import { FormikProvider, useFormik } from "formik";
import { useQueryMemberProfileAsMember } from "libs/api/src/lib/hooks/memberAsMember/useQueryMemberProfile";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import { Button, FormInput } from "../../../components/atoms";
import { buttonVariants } from "../../../components/atoms/buttonVariants";
import { FormPasswordInput } from "../../../components/atoms/form/FormPasswordInput";
import { usePortalRoutes } from "../../../hooks";
import useGymflowModels from "../../../store";
import { ApiResolverContext } from "../../ApiResolverProvider/ApiResolverContext";
import { OnboardingContext } from "../../OnboardingProvider/OnboardingContext";
import { ToastContext } from "../../ToastProvider/context";
import { AuthenticationContext } from "../AuthenticationContext";
import { SessionOverlayContext } from "./SessionOverlayContext";

const signInSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string(),
});

export function SignIn() {
  const { setCurrentScreen } = useContext(OnboardingContext);
  const { sessionOverlayState, setSessionOverlayState } = useContext(
    SessionOverlayContext,
  );
  const { isEmbed } = usePortalRoutes();
  const { t } = useTranslation();
  const clubMetaData = useContext(ApiResolverContext);
  const auth = useContext(AuthenticationContext);
  const { toast } = useContext(ToastContext);
  const { api } = useGymflowModels();
  const profileQuery = useQueryMemberProfileAsMember({
    api,
    memberId: auth.userId,
  });
  const formik = useFormik({
    initialValues: {
      email: sessionOverlayState.state?.email ?? "",
      password: "",
    },
    validationSchema: toFormikValidationSchema(signInSchema),
    validate: toFormikValidate(signInSchema),
    onSubmit: async (values) => {
      const response = await auth.login(
        values.email.trim().toLowerCase(),
        values.password,
      );

      if (response.success) {
        const member = await profileQuery.refetch();
        if (member.data?.onboardStatus === "INCOMPLETE") {
          setCurrentScreen("banner");
        }
        setSessionOverlayState({
          screen: "closed",
          state: {
            email: "",
          },
        });
      } else {
        toast({
          message: t("portal.errorCodes.generic"),
          description: {
            WRONG_CREDENTIALS: t("portal.login.validation.wrongCredentials"),
            WRONG_USER_TYPE: t("portal.login.validation.userNotAMember"),
            WRONG_CLUB: t("portal.login.validation.wrongClub"),
          }[response.error],
          intent: "error",
        });
      }
    },
  });
  return (
    <FormikProvider value={formik}>
      <div
        className={cn(
          "flex w-full max-w-sm flex-col justify-center gap-8 min-h-fit",
          {
            "justify-start": isEmbed,
          },
        )}
      >
        <div className="flex flex-col items-center gap-2">
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-50">
            {t("page.hostedPages.signIn.title")}
          </div>
          <div className="text-center text-sm font-medium text-gray-600 dark:text-gray-400">
            {t("page.hostedPages.signIn.description")}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <FormInput name="email" label={t("page.hostedPages.signIn.email")} />
          <FormPasswordInput
            name="password"
            label={t("page.hostedPages.signIn.password")}
          />
        </div>
        <div className="flex w-full flex-col items-center gap-2">
          <Button
            intent="primary"
            type="submit"
            className="w-full"
            onClick={async () => {
              await formik.submitForm();
            }}
          >
            {t("portal.common.continue")}
          </Button>

          <a
            className={cn(
              buttonVariants({
                intent: "link",
                size: "small",
              }),
              "w-fit px-4",
            )}
            href={`${clubMetaData.urls.auth}/realms/${clubMetaData.authRealm}/login-actions/reset-credentials?client_id=gymflow-app`}
            target="_blank"
            rel="noreferrer"
          >
            {t("page.hostedPages.signIn.forgotPassword")}
          </a>
        </div>
      </div>
    </FormikProvider>
  );
}
